import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, CircleIcon } from 'lucide-react';
interface ChecklistItem {
  id: string;
  text: string;
  completed: boolean;
  category: 'before' | 'during' | 'after';
}
const viewingChecklist: ChecklistItem[] = [
// ÎNAINTE DE VIZIONARE
{
  id: '1',
  text: 'Verifică că toate contractele sunt semnate înainte de vizionare',
  completed: false,
  category: 'before'
}, {
  id: '2',
  text: 'Pregătește documentația necesară (contracte, fișe proprietate)',
  completed: false,
  category: 'before'
}, {
  id: '3',
  text: 'Ajunge la proprietate cu 15-20 minute înaintea clientului',
  completed: false,
  category: 'before'
}, {
  id: '4',
  text: 'Realizează un nou set de poze și video al proprietății',
  completed: false,
  category: 'before'
}, {
  id: '5',
  text: 'Pregătește proprietarul pentru vizionare (ce să spună/facă)',
  completed: false,
  category: 'before'
}, {
  id: '6',
  text: 'Verifică că proprietatea este curată și prezentabilă',
  completed: false,
  category: 'before'
}, {
  id: '7',
  text: 'Deschide ferestrele și aprinde luminile',
  completed: false,
  category: 'before'
},
// ÎN TIMPUL VIZIONĂRII
{
  id: '8',
  text: 'Prezintă proprietatea profesional și evidențiază punctele forte',
  completed: false,
  category: 'during'
}, {
  id: '9',
  text: 'Observă reacțiile clientului și notează feedback-ul',
  completed: false,
  category: 'during'
}, {
  id: '10',
  text: 'Răspunde la toate întrebările clientului',
  completed: false,
  category: 'during'
},
// DUPĂ VIZIONARE
{
  id: '11',
  text: 'Ia rămas bun de la proprietar și spune-i că vei contacta telefonic',
  completed: false,
  category: 'after'
}, {
  id: '12',
  text: 'Conduci clienții și colectează feedback imediat',
  completed: false,
  category: 'after'
}, {
  id: '13',
  text: 'Încearcă să obții o ofertă de la clienți',
  completed: false,
  category: 'after'
}, {
  id: '14',
  text: 'Recalifică clientul (buget, preferințe, urgență)',
  completed: false,
  category: 'after'
}, {
  id: '15',
  text: 'Indexează în platformă rezultatul vizionării',
  completed: false,
  category: 'after'
}, {
  id: '16',
  text: 'Notează feedback-ul detaliat în CRM',
  completed: false,
  category: 'after'
}, {
  id: '17',
  text: 'Contactează proprietarul cu feedback-ul clientului',
  completed: false,
  category: 'after'
}];
export function ViewingChecklist() {
  const [items, setItems] = useState<ChecklistItem[]>(viewingChecklist);
  const toggleItem = (id: string) => {
    setItems(items.map(item => item.id === id ? {
      ...item,
      completed: !item.completed
    } : item));
  };
  const getCategoryTitle = (category: 'before' | 'during' | 'after') => {
    switch (category) {
      case 'before':
        return '📋 Înainte de Vizionare';
      case 'during':
        return '🏠 În Timpul Vizionării';
      case 'after':
        return '✅ După Vizionare';
    }
  };
  const getCategoryColor = (category: 'before' | 'during' | 'after') => {
    switch (category) {
      case 'before':
        return 'from-blue-500 to-indigo-600';
      case 'during':
        return 'from-purple-500 to-pink-600';
      case 'after':
        return 'from-emerald-500 to-green-600';
    }
  };
  const getCategoryBg = (category: 'before' | 'during' | 'after') => {
    switch (category) {
      case 'before':
        return 'bg-blue-50';
      case 'during':
        return 'bg-purple-50';
      case 'after':
        return 'bg-emerald-50';
    }
  };
  const categories: Array<'before' | 'during' | 'after'> = ['before', 'during', 'after'];
  const getCompletedCount = (category: 'before' | 'during' | 'after') => {
    return items.filter(item => item.category === category && item.completed).length;
  };
  const getTotalCount = (category: 'before' | 'during' | 'after') => {
    return items.filter(item => item.category === category).length;
  };
  const overallCompleted = items.filter(item => item.completed).length;
  const overallProgress = overallCompleted / items.length * 100;
  return <div className="space-y-6" data-id="element-7056">
      {/* Overall Progress */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-7057">
        <div className="flex items-center justify-between mb-3" data-id="element-7058">
          <h3 className="text-lg font-bold text-[#132C53]" data-id="element-7059">
            Progres Total Checklist Vizionare
          </h3>
          <span className="text-sm bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-1.5 rounded-full font-bold" data-id="element-7060">
            {overallCompleted}/{items.length}
          </span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-3" data-id="element-7061">
          <motion.div initial={{
          width: 0
        }} animate={{
          width: `${overallProgress}%`
        }} transition={{
          duration: 0.5
        }} className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full" data-id="element-7062" />
        </div>
      </div>

      {/* Checklist by Category */}
      {categories.map((category, categoryIndex) => {
      const categoryItems = items.filter(item => item.category === category);
      const completed = getCompletedCount(category);
      const total = getTotalCount(category);
      const progress = completed / total * 100;
      return <motion.div key={category} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: categoryIndex * 0.1
      }} className={`${getCategoryBg(category)} rounded-2xl p-6 border-2 border-slate-200`} data-id="element-7063">
            <div className="mb-4" data-id="element-7064">
              <div className="flex items-center justify-between mb-2" data-id="element-7065">
                <h4 className="text-lg font-bold text-[#132C53]" data-id="element-7066">
                  {getCategoryTitle(category)}
                </h4>
                <span className={`text-sm bg-gradient-to-r ${getCategoryColor(category)} text-white px-3 py-1 rounded-full font-bold`} data-id="element-7067">
                  {completed}/{total}
                </span>
              </div>
              <div className="w-full bg-white bg-opacity-50 rounded-full h-2" data-id="element-7068">
                <motion.div initial={{
              width: 0
            }} animate={{
              width: `${progress}%`
            }} transition={{
              duration: 0.5,
              delay: categoryIndex * 0.1
            }} className={`bg-gradient-to-r ${getCategoryColor(category)} h-2 rounded-full`} data-id="element-7069" />
              </div>
            </div>

            <div className="space-y-2" data-id="element-7070">
              {categoryItems.map((item, index) => <motion.button key={item.id} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.3,
            delay: categoryIndex * 0.1 + index * 0.05
          }} onClick={() => toggleItem(item.id)} className="w-full flex items-start space-x-3 text-left p-4 rounded-xl bg-white hover:shadow-md transition-all border-2 border-transparent hover:border-slate-300" data-id="element-7071">
                  {item.completed ? <CheckCircleIcon className="w-6 h-6 flex-shrink-0 text-emerald-600" data-id="element-7072" /> : <CircleIcon className="w-6 h-6 flex-shrink-0 text-slate-300" data-id="element-7073" />}
                  <span className={`flex-1 text-sm ${item.completed ? 'text-slate-500 line-through' : 'text-slate-700 font-medium'}`} data-id="element-7074">
                    {item.text}
                  </span>
                </motion.button>)}
            </div>
          </motion.div>;
    })}

      {/* Pro Tips */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-l-4 border-amber-500 p-6 rounded-r-xl" data-id="element-7075">
        <h4 className="font-bold text-amber-900 mb-3 flex items-center space-x-2" data-id="element-7076">
          <span data-id="element-7077">💡</span>
          <span data-id="element-7078">Sfaturi Pro pentru Vizionări</span>
        </h4>
        <ul className="space-y-2 text-sm text-amber-900" data-id="element-7079">
          <li className="flex items-start space-x-2" data-id="element-7080">
            <span className="text-amber-600 mt-0.5" data-id="element-7081">✓</span>
            <span data-id="element-7082">
              Ajunge întotdeauna cu 15-20 min înainte pentru a pregăti spațiul
            </span>
          </li>
          <li className="flex items-start space-x-2" data-id="element-7083">
            <span className="text-amber-600 mt-0.5" data-id="element-7084">✓</span>
            <span data-id="element-7085">
              Fă poze și video fresh la fiecare vizionare pentru documentare
            </span>
          </li>
          <li className="flex items-start space-x-2" data-id="element-7086">
            <span className="text-amber-600 mt-0.5" data-id="element-7087">✓</span>
            <span data-id="element-7088">
              Colectează feedback imediat după vizionare, când e proaspăt
            </span>
          </li>
          <li className="flex items-start space-x-2" data-id="element-7089">
            <span className="text-amber-600 mt-0.5" data-id="element-7090">✓</span>
            <span data-id="element-7091">Indexează rezultatul în platformă în aceeași zi</span>
          </li>
        </ul>
      </div>
    </div>;
}